import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, HardHat, Loader2, Shield, UserCheck } from 'lucide-react'
import { useSystemBranding } from 'shared/branding/SystemBrandingProvider'
import { isAzureConfigured, startAzureLogin } from 'modules/auth/api/azureAuth'
import { BUILD_COMMIT, BUILD_TIME } from 'shared/buildInfo'
import './login-selector.css'

const resolveErrorMessage = (error: any) => {
  const data = error?.response?.data
  return (
    (typeof data === 'string' ? data : null) ??
    (typeof data?.message === 'string' ? data.message : null) ??
    error?.message ??
    'Unable to open contractor login right now.'
  )
}

export function LoginSelectorPage() {
  const navigate = useNavigate()
  const { branding } = useSystemBranding()
  const [isContractorLoading, setIsContractorLoading] = useState(false)
  const [contractorError, setContractorError] = useState('')
  const normalizedEnv = branding.env.trim().toUpperCase()
  const isPrdEnvironment = normalizedEnv === 'PRD'
  const isDevEnvironment = normalizedEnv === 'DEV'
  const deployedLabel = useMemo(() => {
    if (!BUILD_TIME) return null
    const date = new Date(BUILD_TIME)
    if (Number.isNaN(date.getTime())) return null
    const formatted = date.toLocaleString('th-TH', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
    return `Deployed ${formatted} (${BUILD_COMMIT})`
  }, [])

  const handleSelect = async (type: 'employee' | 'contractor') => {
    if (type === 'employee') {
      navigate('/login/employee')
      return
    }

    if (isContractorLoading) {
      return
    }

    setContractorError('')
    setIsContractorLoading(true)

    try {
      const isContractorAzureEnabled = await isAzureConfigured('CONTRACTOR')
      if (isContractorAzureEnabled) {
        await startAzureLogin('CONTRACTOR')
        return
      }

      navigate('/login/contractor')
    } catch (error: any) {
      console.error('Failed to start contractor login flow', error)
      setContractorError(resolveErrorMessage(error))
    } finally {
      setIsContractorLoading(false)
    }
  }

  return (
    <div className="login-selector">
      <div className="login-selector__container">
        {branding.env ? (
          <div
            className={`login-selector__env-badge${isPrdEnvironment ? ' login-selector__env-badge--prd' : ''}${isDevEnvironment ? ' login-selector__env-badge--dev' : ''}`}
            aria-label={`Environment ${branding.env}`}
          >
            {branding.env}
          </div>
        ) : null}

        {deployedLabel ? <p className="login-selector__build-info">{deployedLabel}</p> : null}

        <header className="login-selector__header">
          <div className="login-selector__logo">
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt={branding.systemName} className="login-selector__logo-image" />
            ) : (
              <div className="login-selector__logo-badge">
                <Shield size={40} />
              </div>
            )}
          </div>
          <div>
            <h1>{branding.systemName}</h1>
            <p>{branding.companyName}</p>
          </div>
        </header>

        <section className="login-selector__cards">
          <article className="login-card login-card--employee" onClick={() => void handleSelect('employee')}>
            <div className="login-card__icon">
              <UserCheck size={56} />
            </div>
            <h2>พนักงาน</h2>
            <p className="login-card__subtitle">Employee Login</p>
            <button
              className="login-card__button login-card__button--employee"
              type="button"
              onClick={() => void handleSelect('employee')}
            >
              <UserCheck size={20} />
              เข้าสู่ระบบพนักงาน
            </button>
          </article>

          <article
            className={`login-card login-card--contractor${isContractorLoading ? ' login-card--loading' : ''}`}
            onClick={() => void handleSelect('contractor')}
          >
            <div className="login-card__icon">
              {isContractorLoading ? <Loader2 size={56} className="login-card__spinner" /> : <HardHat size={56} />}
            </div>
            <h2>ผู้รับเหมา</h2>
            <p className="login-card__subtitle">Contractor Login</p>
            <button
              className="login-card__button login-card__button--contractor"
              type="button"
              onClick={() => void handleSelect('contractor')}
              disabled={isContractorLoading}
            >
              {isContractorLoading ? <Loader2 size={20} className="login-card__button-spinner" /> : <HardHat size={20} />}
              {isContractorLoading ? 'Checking sign-in route...' : 'เข้าสู่ระบบผู้รับเหมา'}
            </button>
          </article>
        </section>

        {contractorError ? (
          <div className="login-selector__error" role="alert">
            <AlertTriangle size={18} />
            <span>{contractorError}</span>
          </div>
        ) : null}
      </div>
    </div>
  )
}
